import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Shield, KeyRound, Crown, LogOut, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import MasterAdmin from './MasterAdmin';

const MasterDashboardPage = () => {
  const [step, setStep] = useState('login'); // 'login', 'verify', 'dashboard'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sentCode, setSentCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [masterUser, setMasterUser] = useState(null);
  
  const { login, logout, user, API } = useContext(AuthContext);
  const navigate = useNavigate();

  // Check if already authenticated as master admin
  useEffect(() => {
    if (user && user.email === 'admin@reqtrak.com') {
      setStep('verify'); // Still need code verification even if logged in
      sendVerificationCode();
    }
  }, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      const { access_token, user: userData } = response.data;
      
      // Check if user is master admin
      if (userData.email !== 'admin@reqtrak.com') {
        setError('Access denied. Master Dashboard requires ReqTrak Master credentials.');
        setLoading(false);
        return;
      }

      login(access_token, userData);
      setMasterUser(userData);
      setStep('verify');
      toast.success('Login successful! Please verify with code.');
      
      // Send verification code
      await sendVerificationCode();

    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const sendVerificationCode = async () => {
    try {
      // For Master Dashboard, we'll use a simple 6-digit code generation
      // In production, this would send an actual email
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      setSentCode(code); // For development - shows the code
      setTimeRemaining(15 * 60); // 15 minutes
      
      // Start countdown timer
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      toast.success('Master verification code generated!');
    } catch (error) {
      console.error('Error generating verification code:', error);
      toast.error('Failed to generate verification code');
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // For Master Dashboard, verify against the generated code
      if (verificationCode === sentCode) {
        setStep('dashboard');
        toast.success('Access granted to Master Dashboard!');
      } else {
        throw new Error('Invalid verification code');
      }

    } catch (error) {
      const errorMessage = error.message || 'Invalid verification code.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      // Generate new code
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      setSentCode(code);
      setTimeRemaining(15 * 60);
      setVerificationCode('');
      toast.success('New verification code generated!');
    } catch (error) {
      toast.error('Failed to generate new code');
    } finally {
      setLoading(false);
    }
  };

  const handleMasterLogout = () => {
    logout();
    setStep('login');
    setEmail('');
    setPassword('');
    setVerificationCode('');
    setMasterUser(null);
    toast.success('Logged out from Master Dashboard');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (step === 'dashboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
        {/* Master Dashboard Header */}
        <header className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_policerecords-2/artifacts/crx2tbck_RequestTrack%20logo.png" 
                  alt="ReqTrak Logo" 
                  className="h-20 w-20 object-contain"
                />
                <div>
                  <h1 className="text-xl font-semibold text-slate-800">ReqTrak Platform</h1>
                  <p className="text-sm text-slate-600">Master Dashboard</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 hover:text-blue-600"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to Department
                </Button>
                
                <div className="flex items-center gap-2 text-slate-700">
                  <Crown className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium">ReqTrak Master</span>
                </div>
                
                <Button 
                  onClick={handleMasterLogout}
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 hover:text-red-600"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden md:inline ml-1">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Master Dashboard Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <MasterAdmin />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_policerecords-2/artifacts/crx2tbck_RequestTrack%20logo.png" 
            alt="ReqTrak Logo" 
            className="h-24 w-24 mx-auto object-contain mb-4"
          />
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Master Dashboard</h1>
          <p className="text-slate-600">ReqTrak Platform Management</p>
        </div>

        <Card className="shadow-2xl border-0">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold text-center flex items-center justify-center gap-2">
              <Crown className="w-6 h-6 text-purple-600" />
              {step === 'login' ? 'Master Access' : 'Verify Identity'}
            </CardTitle>
            <CardDescription className="text-center">
              {step === 'login' 
                ? 'Enter ReqTrak Master credentials'
                : 'Enter the verification code sent to your email'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'login' ? (
              <form onSubmit={handleLogin} className="space-y-6">
                {error && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertDescription className="text-red-700">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Master Email
                  </Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 border-slate-200 focus:border-purple-500 focus:ring-purple-500"
                      placeholder="admin@reqtrak.com"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                    Master Password
                  </Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-12 border-slate-200 focus:border-purple-500 focus:ring-purple-500"
                      placeholder="Enter master password"
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="spinner"></div>
                      Authenticating...
                    </div>
                  ) : (
                    <>
                      <Crown className="w-4 h-4 mr-2" />
                      Access Master Dashboard
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode} className="space-y-6">
                {error && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertDescription className="text-red-700">{error}</AlertDescription>
                  </Alert>
                )}

                {/* Development: Show the code */}
                {sentCode && (
                  <Alert className="border-purple-200 bg-purple-50">
                    <AlertDescription className="text-purple-700">
                      <strong>Development Mode:</strong> Your verification code is <strong>{sentCode}</strong>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="verificationCode" className="text-sm font-medium text-slate-700">
                    Verification Code
                  </Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="verificationCode"
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="pl-10 h-12 border-slate-200 focus:border-purple-500 focus:ring-purple-500 text-center text-lg tracking-widest"
                      placeholder="000000"
                      maxLength={6}
                      required
                    />
                  </div>
                  <p className="text-xs text-slate-500">Enter the 6-digit code sent to admin@reqtrak.com</p>
                </div>

                {timeRemaining > 0 && (
                  <div className="text-center text-sm text-slate-600">
                    Code expires in: <span className="font-mono font-bold">{formatTime(timeRemaining)}</span>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="spinner"></div>
                      Verifying...
                    </div>
                  ) : (
                    <>
                      <Shield className="w-4 h-4 mr-2" />
                      Verify & Access
                    </>
                  )}
                </Button>

                <div className="text-center">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleResendCode}
                    disabled={loading || timeRemaining > 0}
                    className="text-purple-600 hover:text-purple-700"
                  >
                    {timeRemaining > 0 ? `Wait ${formatTime(timeRemaining)}` : 'Resend Code'}
                  </Button>
                </div>
              </form>
            )}

            <div className="mt-6 text-center">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="text-slate-600 hover:text-slate-700"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Department Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MasterDashboardPage;